import type { TargetTaxonomyKey } from '../../Generated/TargetTaxonomy.ts'
import type { IOrder, ISamplePool } from '../../Model/index.ts'
import type { DraftState, TargetCheck } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { MismatchingAmpliconError, validatePrimers } from '../../View/Page/OrderManagementPage/OrderDetail/PrimerValidation.tsx'
import { placedGroups } from './pools.ts'
import { primersFor } from './sheet/applyPlan.ts'

export type PrimerPair = { forward: string; reverse: string }

/**
 * The primers submit validates the target with: the classic check takes the first sample of the first
 * pool, so here the first sample in pool 1 by file order, with the primers the wizard writes into it.
 * `undefined` when it has none the wizard knows (primers set only in the order are not in the draft).
 */
export const submitPrimers = (draft: DraftState): PrimerPair | undefined => {
  const first = placedGroups(draft).find((group) => !group.pool && group.itemType !== 'NEGATIVE_CONTROL')
  const primers = first && !first.primersOnServer ? primersFor(draft, first) : {}
  return primers.forward && primers.reverse ? { forward: primers.forward, reverse: primers.reverse } : undefined
}

/**
 * Whether submit would refuse `target` for `primers` as registered for another amplicon, decided by the
 * classic submit's own validation: a pair registered for several amplicons fits each of them. Any other
 * outcome (fits, unknown primer, the registry unreachable) is no contradiction.
 */
export const checkTarget = async (
  target: TargetTaxonomyKey,
  primers: PrimerPair,
  validate: typeof validatePrimers = validatePrimers
): Promise<TargetCheck | undefined> => {
  const order = { targetTaxonomy: target } as IOrder
  const pools = [
    { samples: [{ forwardPrimer: primers.forward, reversePrimer: primers.reverse }], negativeControls: [] }
  ] as unknown as ISamplePool[]
  try {
    await validate(order, pools)
    return undefined
  } catch (error) {
    if (!(error instanceof MismatchingAmpliconError)) {
      return undefined
    }
    return { target, ...primers, registeredFor: [...new Set(error.foundPrimers.map((primer) => primer.amplicon as TargetTaxonomyKey))] }
  }
}

/** The check as it applies to the draft now: for its current target and the primers submit would take. */
export const currentTargetCheck = (draft: DraftState): TargetCheck | undefined => {
  const check = draft.targetCheck
  const primers = check && submitPrimers(draft)
  return check &&
    primers &&
    check.target === draft.settings?.targetTaxonomy.value &&
    check.forward === primers.forward &&
    check.reverse === primers.reverse
    ? check
    : undefined
}
